/**
 * The Blocks panel's Team blocks tab: the team library (blocks/list, archived hidden), searchable and
 * grouped by category. Click or drag inserts a copy (blocks/teamBlocks.ts insertTeamBlock); each item
 * expands to its preview's first page.
 */
import { useCallback, useEffect, useState } from 'react'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { blocksApiService, type ContentBlockSummary } from '../../services/blocksApiService'
import { ApiError } from '../../services/BaseApiService'
import { setTeamBlockInsertHandler, writeTeamBlock } from '../../blocks/paletteInsert'
import { insertTeamBlock, matchesBlock, type NoteSink } from '../../blocks/teamBlocks'
import BlockThumbnail from '../blocks/BlockThumbnail'

interface Props {
  editor: Editor | null
  templateId: string
  /** The version open in the editor, for loading an inserted block's images; null on a never-saved template. */
  versionId?: string | null
  canEdit: boolean
  /** Bumped by the page after a save, to refetch. */
  refreshKey?: number
  /** Where an inserted block's footnotes/endnotes/comments go. */
  noteSink?: NoteSink
}

const UNCATEGORIZED = 'Uncategorized'

export default function TeamBlocksTab({ editor, templateId, versionId, canEdit, refreshKey = 0, noteSink }: Props) {
  const [blocks, setBlocks] = useState<ContentBlockSummary[] | null>(null)
  const [loadError, setLoadError] = useState('')
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoadError('')
    blocksApiService.list()
      .then(b => { if (!cancelled) setBlocks(b.filter(x => !x.archived)) })
      .catch(err => { if (!cancelled) { setBlocks([]); setLoadError(err instanceof ApiError ? err.message : 'Could not load team blocks.') } })
    return () => { cancelled = true }
  }, [refreshKey])

  const insert = useCallback((contentBlockId: string, pos?: number) => {
    if (!editor || !canEdit) return
    setBusy(true)
    setError('')
    insertTeamBlock(editor, templateId, contentBlockId, pos, { versionId, notes: noteSink })
      .then(ok => { if (!ok) setError('This block could not be inserted here.') })
      .catch(err => setError(err instanceof ApiError ? err.message : 'Inserting the block failed.'))
      .finally(() => setBusy(false))
  }, [editor, templateId, versionId, canEdit, noteSink])

  useEffect(() => {
    if (!editor || !canEdit) return
    setTeamBlockInsertHandler(editor, insert)
    return () => setTeamBlockInsertHandler(editor, null)
  }, [editor, canEdit, insert])

  const results = (blocks ?? []).filter(b => matchesBlock(b, query))
  const categories = [...new Set(results.map(b => b.category || UNCATEGORIZED))]
    .sort((a, b) => (a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : a.localeCompare(b)))

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: `${spacing.sm} ${spacing[3]}` }}>
      <input
        type="search"
        aria-label="Search team blocks"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search team blocks"
        style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none' }}
      />
      <div style={{ color: colors.textMuted, fontSize: fontSize.xs, margin: `${spacing.sm} 0` }}>
        {busy ? 'Inserting…' : canEdit ? 'Drag into the document, or click to insert after the cursor.' : 'Viewers can browse team blocks but not insert them.'}
      </div>
      {error && <div role="alert" style={{ color: colors.error, fontSize: fontSize.xs, marginBottom: spacing.sm }}>{error}</div>}
      {loadError && <div role="alert" style={{ color: colors.error, fontSize: fontSize.xs }}>{loadError}</div>}
      {blocks === null && <div style={{ color: colors.textMuted }}>Loading…</div>}
      {blocks?.length === 0 && !loadError && (
        <div data-testid="team-blocks-empty" style={{ color: colors.textMuted, textAlign: 'center', padding: `${spacing.md} 0`, lineHeight: 1.5 }}>
          No team blocks yet.
          <div style={{ fontSize: fontSize.xs, marginTop: spacing.sm }}>
            To save one, select content in the document and choose <strong>Save as block</strong> in the toolbar, or
            open a block's ⋮⋮ handle and choose <strong>Save as block…</strong>.
          </div>
        </div>
      )}
      {!!blocks?.length && results.length === 0 && <div style={{ color: colors.textMuted, padding: '3px 0' }}>No team blocks match “{query}”.</div>}
      {categories.map(cat => (
        <section key={cat} aria-label={cat}>
          <div style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', margin: `${spacing[3]} 0 ${spacing.xs}` }}>{cat}</div>
          {results.filter(b => (b.category || UNCATEGORIZED) === cat).map(b => (
            <TeamBlockItem
              key={b.contentBlockId}
              block={b}
              canEdit={canEdit && !!editor}
              expanded={expanded === b.contentBlockId}
              onToggle={() => setExpanded(e => (e === b.contentBlockId ? null : b.contentBlockId))}
              onInsert={() => insert(b.contentBlockId)}
            />
          ))}
        </section>
      ))}
    </div>
  )
}

function TeamBlockItem({ block, canEdit, expanded, onToggle, onInsert }: {
  block: ContentBlockSummary
  canEdit: boolean
  expanded: boolean
  onToggle: () => void
  onInsert: () => void
}) {
  const vars = block.variables?.length ?? 0
  const sub = block.description || (vars ? `${vars} field${vars === 1 ? '' : 's'}` : 'No fields')
  return (
    <div style={{ borderRadius: radius.sm }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div
          role="button"
          tabIndex={canEdit ? 0 : -1}
          aria-disabled={!canEdit}
          draggable={canEdit}
          data-team-block-id={block.contentBlockId}
          title={canEdit ? `Insert ${block.name}` : block.name}
          onDragStart={e => writeTeamBlock(e.dataTransfer, block.contentBlockId)}
          onClick={() => { if (canEdit) onInsert() }}
          onKeyDown={e => { if (canEdit && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onInsert() } }}
          style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '5px 6px', borderRadius: radius.sm, cursor: canEdit ? 'grab' : 'default', userSelect: 'none' }}
          onMouseEnter={e => { e.currentTarget.style.background = colors.surfaceMist }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
        >
          <span style={{ color: colors.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.name}</span>
          <span style={{ color: colors.textMuted, fontSize: fontSize.xs, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>
        </div>
        <button
          type="button"
          aria-label={`Preview ${block.name}`}
          aria-expanded={expanded}
          onClick={onToggle}
          title="Preview"
          style={{ flex: '0 0 auto', border: 'none', background: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: fontSize.xs, padding: '4px 6px' }}
        >
          {expanded ? '▾' : '▸'}
        </button>
      </div>
      {expanded && (
        <div style={{ margin: '2px 6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
          <BlockThumbnail contentBlockId={block.contentBlockId} revision={block.updatedAt} name={block.name} width={220} />
          {!!block.tags?.length && (
            <div style={{ padding: '4px 6px', color: colors.textMuted, fontSize: fontSize.xs, borderTop: `1px solid ${colors.border}` }}>{block.tags.join(' · ')}</div>
          )}
        </div>
      )}
    </div>
  )
}
